import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Send, Loader2, AlertCircle, UserCheck, Clock, UserX, Users } from "lucide-react";
import ShareInviteSheet from "../components/instructor/ShareInviteSheet";
import { useVisibilityRefetch } from "../hooks/useVisibilityRefetch";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── Invite continuity (Feb 2026) ──────────────────────────────────
// Map a work_students row's invite fields to a single operational
// state-tag the card renders. Pure derivation, no side-effects.
//   joined        — linked_user_id set & status=completed (happy path)
//   pending       — status=invited|joined and within INVITE_FRESH_DAYS
//   expired       — status=invited|joined but invited_at > INVITE_FRESH_DAYS ago
//   needs_reconnect — status=onboarded/completed but linked_user_id null
//                     (the "stuck" cohort — same backstop as preflight)
//   not_joined    — status=none / null and no linked_user_id
//
// The doctrine pin file source-pins these literal tags.
const INVITE_FRESH_DAYS = 14;

function deriveInviteState(s) {
  const status = (s?.invite_status || "").toLowerCase();
  const linked = Boolean(s?.linked);
  if (linked && status === "completed") return "joined";
  if (!linked && (status === "onboarded" || status === "completed")) return "needs_reconnect";
  if (["prepared", "invited", "opened", "joined"].includes(status)) {
    const ts = s?.invited_at ? Date.parse(s.invited_at) : 0;
    if (ts > 0) {
      const ageDays = (Date.now() - ts) / 86400000;
      if (ageDays > INVITE_FRESH_DAYS) return "expired";
    }
    return "pending";
  }
  return "not_joined";
}

function InviteStatePill({ state }) {
  if (!state) return null;
  const MAP = {
    joined:          { label: "Joined",            cls: "bg-emerald-900/40 text-emerald-300 ring-emerald-800/40", Icon: UserCheck },
    pending:         { label: "Invite pending",    cls: "bg-blue-900/40    text-blue-300    ring-blue-800/40",    Icon: Clock },
    expired:         { label: "Invite expired",    cls: "bg-amber-900/40   text-amber-300   ring-amber-800/40",   Icon: AlertCircle },
    needs_reconnect: { label: "Needs reconnect",   cls: "bg-amber-900/40   text-amber-300   ring-amber-800/40",   Icon: AlertCircle },
    not_joined:     { label: "Not joined yet",     cls: "bg-slate-700/60   text-slate-300   ring-slate-600/40",   Icon: UserX },
  };
  const { label, cls, Icon } = MAP[state] || MAP.not_joined;
  return (
    <span
      data-testid={`invite-state-${state}`}
      className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full ring-1 ${cls}`}
    >
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

function SkillCircle({ score, size = 40 }) {
  const r = (size - 6) / 2;
  const circ = 2 * Math.PI * r;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="-rotate-90" viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#334155" strokeWidth="3" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke={score >= 80 ? "#e2e8f0" : score >= 40 ? "#94a3b8" : "#475569"}
          strokeWidth="3" strokeLinecap="round"
          strokeDasharray={`${(score / 100) * circ} ${circ}`}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-white">{score}%</span>
    </div>
  );
}

function StudentOverviewCard({ s, onAwardReward, navigate, onResendInvite }) {
  const [showRewards, setShowRewards] = useState(false);
  const [awarding, setAwarding] = useState(false);
  // Invite continuity state — derived per render, no extra fetch.
  const inviteState = deriveInviteState(s);
  const canResend = inviteState !== "joined";
  const inviteActionLabel = inviteState === "not_joined"
    ? "Invite to NDG"
    : "Share invite again";
  const [resending, setResending] = useState(false);

  const handleResend = async (e) => {
    e.stopPropagation();
    if (resending) return;
    setResending(true);
    try {
      await onResendInvite(s.student_id, s);
    } finally {
      setResending(false);
    }
  };

  const handleAward = async (rewardId) => {
    setAwarding(true);
    await onAwardReward(s.student_id, rewardId);
    setAwarding(false);
    setShowRewards(false);
  };

  const REWARD_OPTIONS = [
    { id: "free_lesson_time", label: "+10 Min Free Time" },
    { id: "priority_booking", label: "Priority Booking" },
    { id: "lesson_discount", label: "Lesson Discount" },
    { id: "progress_recognition", label: "Progress Recognition" },
  ];

  return (
    <div
      data-testid={`overview-card-${s.student_id}`}
      className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden"
    >
      {/* Header */}
      <div
        className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-750"
        onClick={() => navigate(`/student-history/${s.student_id}`)}
      >
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-white text-sm">{s.name}</p>
          <p className="text-[10px] text-slate-500">{s.stage} · {s.total_points} pts</p>
          {/* Invite continuity (Feb 2026) — operational state visible
              where the instructor naturally looks. The pill renders
              one of: joined / pending / expired / needs_reconnect /
              not_joined. */}
          <div className="mt-1.5">
            <InviteStatePill state={inviteState} />
          </div>
        </div>
        {s.today.red_button && (
          <div className="w-7 h-7 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
            <div className="w-3.5 h-3.5 bg-white rounded-full" />
          </div>
        )}
      </div>

      {/* Invite action — visible on every non-joined card.
          Single mutation surface = the already-supported
          POST /api/work-mode/students/{id}/invite endpoint (no new
          backend route, no duplicate-pupil creation, no schema
          change). Doctrine-locked by
          tests/test_pupil_card_resend_invite_doctrine.py. */}
      {canResend && (
        <div className="px-4 pb-3 -mt-1">
          <button
            data-testid={`resend-join-invite-btn-${s.student_id}`}
            onClick={handleResend}
            disabled={resending}
            className="w-full h-10 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-bold inline-flex items-center justify-center gap-2 transition"
          >
            {resending ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending…</>
            ) : (
              <><Send className="w-3.5 h-3.5" /> {inviteActionLabel}</>
            )}
          </button>
        </div>
      )}

      <div className="px-4 pb-4 space-y-3">
        {/* Today's Status */}
        {s.linked && (
          <div className="flex flex-wrap gap-1.5">
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${s.today.checkin_done ? "bg-emerald-900/40 text-emerald-400" : "bg-slate-700 text-slate-500"}`}>
              {s.today.checkin_done ? "Checked in" : "No check-in"}
            </span>
            {s.today.support_level && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-900/40 text-blue-400">
                {s.today.support_level}
              </span>
            )}
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${s.today.prep_done ? "bg-emerald-900/40 text-emerald-400" : "bg-slate-700 text-slate-500"}`}>
              {s.today.prep_done ? "Prepared" : "Not prepared"}
            </span>
          </div>
        )}

        {/* Skill Progress */}
        {Object.values(s.skill_scores).some((v) => v > 0) && (
          <div className="flex items-center gap-2 overflow-x-auto py-1">
            {Object.entries(s.skill_scores).map(([skill, score]) => (
              score > 0 && (
                <div key={skill} className="flex flex-col items-center flex-shrink-0">
                  <SkillCircle score={score} />
                  <span className="text-[8px] text-slate-500 mt-0.5 max-w-[40px] text-center leading-tight truncate">{skill.split(" ")[0]}</span>
                  {score >= 80 && <span className="text-[7px] text-slate-300 font-semibold">Ready</span>}
                </div>
              )
            ))}
          </div>
        )}

        {/* Reflection Patterns */}
        {s.reflection_summary.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {s.reflection_summary.map((p, i) => (
              <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-purple-900/30 text-purple-300 border border-purple-800/30">
                {p}
              </span>
            ))}
          </div>
        )}

        {/* Adaptive Insight */}
        {s.adaptive_insight?.message && s.adaptive_insight.pattern !== "steady" && s.adaptive_insight.pattern !== "new" && (
          <div data-testid={`adaptive-insight-${s.student_id}`} className="bg-slate-800/50 rounded-lg px-3 py-2 border border-slate-700/50">
            <p className="text-[11px] text-slate-300 leading-relaxed">{s.adaptive_insight.message}</p>
          </div>
        )}

        {/* Suggestions */}
        {s.suggestions.length > 0 && (
          <div>
            <ul className="space-y-0.5">
              {s.suggestions.slice(0, 3).map((tip, i) => (
                <li key={i} className="text-[11px] text-slate-400 flex items-start gap-1.5">
                  <span className="text-slate-600 mt-px">-</span>{tip}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Next Focus + Rewards */}
        <div className="flex items-center justify-between pt-1">
          {s.next_focus ? (
            <span className="text-[10px] text-slate-400">Next: <span className="text-white font-medium">{s.next_focus}</span></span>
          ) : (
            <span className="text-[10px] text-slate-500">No focus set</span>
          )}
          <button
            data-testid={`reward-btn-${s.student_id}`}
            onClick={(e) => { e.stopPropagation(); setShowRewards(!showRewards); }}
            className="text-[10px] text-emerald-400 hover:text-emerald-300"
          >
            {showRewards ? "Cancel" : "Reward"}
          </button>
        </div>

        {/* Reward Picker */}
        {showRewards && (
          <div className="grid grid-cols-2 gap-1.5">
            {REWARD_OPTIONS.map((r) => (
              <button
                key={r.id}
                disabled={awarding}
                onClick={(e) => { e.stopPropagation(); handleAward(r.id); }}
                className="text-[10px] text-left p-2 rounded-lg border border-slate-600 hover:border-emerald-500 hover:bg-emerald-900/20 text-slate-400 transition-all"
              >
                {r.label}
              </button>
            ))}
          </div>
        )}

        {/* Existing rewards */}
        {s.rewards.filter((r) => r.status === "unused").length > 0 && !showRewards && (
          <div className="flex flex-wrap gap-1">
            {s.rewards.filter((r) => r.status === "unused").map((r, i) => (
              <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-900/30 text-emerald-400 border border-emerald-800/30">
                {r.reward_label}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function InstructorOverview() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bulkPreviewOpen, setBulkPreviewOpen] = useState(false);

  const fetchOverview = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/work-mode/dashboard-overview`, { withCredentials: true });
      setStudents(res.data.students || []);
    } catch {
      navigate("/work-mode");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);
  useVisibilityRefetch(fetchOverview, { marker: "instructor-overview" });

  const handleAwardReward = async (studentId, rewardId) => {
    try {
      await axios.post(`${API_URL}/api/work-mode/students/${studentId}/rewards`, { reward_id: rewardId }, { withCredentials: true });
      fetchOverview();
    } catch {}
  };

  // Invite continuity (Feb 2026) — Resend handler used by every
  // pupil card. Uses the EXISTING `POST /students/{id}/invite`
  // endpoint that mints a fresh PIN/token (it has an is_stuck branch
  // that recycles the code if the row is in the
  // onboarded/completed-but-unlinked "stuck" state).
  //
  // Truth-fix (Path A, Feb 2026) — this endpoint does NOT send any
  // outbound message. It returns `delivery: "manual_share"` and
  // pre-built share strings. We open the ShareInviteSheet so the
  // instructor sends via their own device. Doctrine-pinned:
  //   tests/test_pupil_card_resend_invite_doctrine.py
  const [shareInvite, setShareInvite] = useState(null);
  const handleResendInvite = async (studentId, student) => {
    try {
      const r = await axios.post(
        `${API_URL}/api/work-mode/students/${studentId}/invite`,
        {},
        { withCredentials: true },
      );
      const delivery = r.data?.delivery || "manual_share";
      if (delivery === "manual_share") {
        toast.success("Invite refreshed · share to send", {
          description: "Pick a channel — your device sends it.",
        });
        setShareInvite(r.data);
      } else {
        // Future Path B: real provider delivered the message.
        toast.success("Invite delivered", {
          description: `via ${delivery}`,
        });
      }
      fetchOverview();
    } catch (e) {
      toast.error("Couldn't refresh invite", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  };

  const bulkEligible = students.filter((s) => deriveInviteState(s) !== "joined");

  const openBulkInvitePreview = () => {
    // Deliberately preview-only. Opening this control must never mint, refresh,
    // or send an invite. Individual invite creation remains an explicit action.
    setBulkPreviewOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white" />
      </div>
    );
  }

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-900 text-white">
      <header className="px-4 py-4 flex items-center gap-3 border-b border-slate-800">
        <button onClick={() => navigate("/work-mode")} className="text-slate-400 hover:text-white">
          <ArrowLeft className="w-6 h-6" />
        </button>
        <div>
          <h1 className="font-bold text-lg">Learner Overview</h1>
          <p className="text-xs text-slate-400">{students.length} learner{students.length !== 1 ? "s" : ""}</p>
        </div>
      </header>

      <div className="px-4 py-4 space-y-3 max-w-lg mx-auto">
        {bulkEligible.length > 0 && (
          <button
            data-testid="bulk-invite-pupils-btn"
            onClick={openBulkInvitePreview}
            className="w-full min-h-12 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold inline-flex items-center justify-center gap-2 px-4"
          >
            <Users className="w-4 h-4" /> Invite pupils
          </button>
        )}
        {bulkPreviewOpen && (
          <div data-testid="bulk-invite-preview" className="rounded-2xl border border-slate-700 bg-slate-800 p-4 space-y-3">
            <div>
              <p className="font-bold">Bulk invite preview</p>
              <p className="text-xs text-slate-400 mt-1">
                {bulkEligible.length} pupil{bulkEligible.length !== 1 ? "s" : ""} can be invited. Nothing has been sent.
              </p>
            </div>
            <div className="max-h-48 overflow-y-auto space-y-2">
              {bulkEligible.map((s) => (
                <div key={s.student_id} className="flex items-center justify-between gap-3 text-sm border-b border-slate-700/70 pb-2">
                  <span className="truncate">{s.name}</span>
                  <InviteStatePill state={deriveInviteState(s)} />
                </div>
              ))}
            </div>
            <p className="text-xs text-amber-300">
              Bulk sending is intentionally disabled until a final send-confirmation step is connected. Importing pupils never sends an invite.
            </p>
            <button
              data-testid="bulk-invite-close-btn"
              onClick={() => setBulkPreviewOpen(false)}
              className="w-full h-10 rounded-xl bg-slate-700 hover:bg-slate-600 text-white text-sm font-bold"
            >
              Close
            </button>
          </div>
        )}
        {students.length === 0 && (
          <p className="text-center text-slate-500 py-8">No learners yet.</p>
        )}
        {students.map((s) => (
          <StudentOverviewCard
            key={s.student_id}
            s={s}
            onAwardReward={handleAwardReward}
            onResendInvite={handleResendInvite}
            navigate={navigate}
          />
        ))}
      </div>
      <ShareInviteSheet
        open={Boolean(shareInvite)}
        invite={shareInvite}
        onClose={() => setShareInvite(null)}
      />
    </div>
  );
}
